import ZipCodesRepository from 'api/zipCode/ZipCodesRepository'
import { States } from 'containers/ApplicantInformation/States'
import { FieldInstruction } from 'containers/FormBuilder'
import InputField from 'form/fieldComponents/InputField'
import SelectField, {
    SelectFieldOnChange,
} from 'form/fieldComponents/SelectField'
import {
    FIELD_REQUIRED,
    FIELD_TOO_LONG,
    FIELD_INVALID_STATE,
    FIELD_PO_BOX_NOT_ALLOWED,
} from 'form/validation/validationMessages'
import { isPOBox } from 'form/validation/validationUtils'
import * as Yup from 'yup'

export type ZipCityStateFieldNames = {
    state: string
    city: string
}

const stateValues = States.reduce<string[]>((values, currentStateData) => {
    values.push(currentStateData.value)

    return values
}, [])

function stateIsWithinKnownValues(state: string) {
    return stateValues.includes(state)
}

function autoPopulateCityAndSateByZip(
    fieldNames: ZipCityStateFieldNames,
): SelectFieldOnChange {
    const zipCodesRepositoryInstance = new ZipCodesRepository(
        window.localStorage,
    )

    return (value, fieldName, formValues, fieldKit) => {
        const zipCodeLength = 5

        if (value.length === zipCodeLength) {
            zipCodesRepositoryInstance.getZipCodeMap().then((zipCodeMap) => {
                const zipCodeInfo = zipCodeMap[value]

                if (zipCodeInfo) {
                    fieldKit.setFieldValue(fieldNames.city, zipCodeInfo.city)
                    if (stateIsWithinKnownValues(zipCodeInfo.state)) {
                        fieldKit.setFieldValue(
                            fieldNames.state,
                            zipCodeInfo.state,
                        )
                    }
                }
            })
        }

        fieldKit.setFieldValue(fieldName, value)
    }
}

const streetAddressValidator = Yup.string()
    .required(FIELD_REQUIRED)
    .min(2)
    .max(121, FIELD_TOO_LONG(121))
    .matches(
        /^[A-Za-z0-9 ]*$/,
        'Address can only contain letters and numbers. ',
    )

export const streetAddress: FieldInstruction<typeof InputField> = {
    Component: InputField,
    config: {
        label: 'Address',
        validator: streetAddressValidator,
    },
}

export const streetAddressAutomobile: FieldInstruction<typeof InputField> = {
    Component: InputField,
    config: {
        ...streetAddress.config,
        validator: streetAddressValidator.test(
            'no-po-box',
            FIELD_PO_BOX_NOT_ALLOWED,
            (value) => {
                if (!value) return true

                return !isPOBox(value)
            },
        ),
    },
}

export const apartmentSuite: FieldInstruction<typeof InputField> = {
    Component: InputField,
    config: {
        label: 'Apartment/Suite',
        validator: Yup.string()
            .max(20, FIELD_TOO_LONG(20))
            .matches(
                /^[A-Za-z0-9 ]*$/,
                "Apartment/Suite can't contain special characters",
            ),
    },
}

export const zipCode: FieldInstruction<typeof InputField> = {
    Component: InputField,
    config: {
        label: 'ZIP Code',
        validator: Yup.string()
            .matches(/^\d*$/, 'Can only contain digits.')
            .max(5, 'Zip codes can only be 5 digits.')
            .min(5, 'Zip codes can only be 5 digits.')
            .required(FIELD_REQUIRED),
    },
}

export function createZipFieldInstruction(
    fieldNames: ZipCityStateFieldNames,
): FieldInstruction<typeof InputField> {
    return {
        Component: InputField,
        config: {
            ...zipCode.config,
            onChange: autoPopulateCityAndSateByZip(fieldNames),
        },
    }
}

export const city: FieldInstruction<typeof InputField> = {
    Component: InputField,
    config: {
        label: 'City',
        validator: Yup.string()
            .matches(
                /^[A-Za-z ]*$/,
                'City can only contain letters. If a city has a number in it, please spell it out.',
            )
            .required(FIELD_REQUIRED)
            .min(2)
            .max(121),
    },
}

export const createStateValidator = (akHiEnabled: boolean) =>
    Yup.string()
        .required(FIELD_REQUIRED)
        .notOneOf(
            akHiEnabled ? ['DC'] : ['AK', 'HI', 'DC'],
            FIELD_INVALID_STATE,
        )

const stateValidator = createStateValidator(false)

export const state: FieldInstruction<typeof SelectField> = {
    Component: SelectField,
    config: {
        label: 'State',
        placeholder: 'Select State',
        options: States,
        validator: stateValidator,
    },
}

export const testExports = {
    stateIsWithinKnownValues,
}
